import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGS } from "@/lib/i18n";

/**
 * LanguageSwitcher — writes `?lang=<code>` (or removes it for English) and
 * lets useLangSync (mounted in Layout) drive everything downstream: it flips
 * i18n, updates <html lang|dir>, and the HreflangTags effect follows.
 *
 * Keeping the URL as the single source of truth eliminates any race between
 * DOM writes and React re-renders.
 */
export default function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();

    const set = (code) => {
        const params = new URLSearchParams(search);
        if (code === "en") params.delete("lang");
        else params.set("lang", code);
        const q = params.toString();
        navigate({ pathname, search: q ? `?${q}` : "" }, { replace: true });
        // Nudge i18n immediately so the switcher's own active-state and any
        // translated text in the header update on the same frame as the click.
        // useLangSync will re-affirm on the next tick.
        if (i18n.language?.split("-")[0] !== code) i18n.changeLanguage(code);
    };

    const active = (i18n.language || "en").split("-")[0];

    return (
        <div className="flex items-center gap-1 text-[11px] tracking-widest" data-testid="lang-switcher">
            {LANGS.map((l, i) => (
                <span key={l.code} className="flex items-center">
                    <button
                        onClick={() => set(l.code)}
                        data-testid={`lang-${l.code}`}
                        className={`px-1.5 py-0.5 transition-colors relative ${active === l.code ? "brand-gradient-text font-medium" : "text-muted-foreground hover:text-foreground"}`}
                        aria-label={l.name}
                    >
                        {l.label}
                    </button>
                    {i < LANGS.length - 1 && <span className="text-muted-foreground/40">/</span>}
                </span>
            ))}
        </div>
    );
}
